import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router-dom';
import Alert from 'react-s-alert';

import withProfile from 'hocs/withProfile';

import Posts from 'api/resources/posts';
import getErrors, { processErrorByAlert, isCode4 } from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import PublicationForm from 'admin-editor/components/PublicationForm';

import Loader from 'components/Loader';

import Main from 'styled/Page/Main';
import CenteredContainer from 'styled/Page/CenteredContainer';
import ErrorText from 'styled/Page/ErrorText';

export class PublicationPage extends React.PureComponent {
  state = {
    loading: false,
    errors: [],
    loadingSave: false,
    isPinModalOpen: false,
    shouldUnlockOnUnmount: false,
  };

  componentDidMount() {
    this.loadPublication();
  }

  get title() {
    return this.state.publication
      ? this.state.publication.title
      : this.props.match.params.publicationId;
  }

  get snoozes() {
    return this.state.publication.itemSnoozes
      ? this.state.publication.itemSnoozes.map((item) => ({
          id: item.snooze.id,
          name: item.snooze.name,
        }))
      : [];
  }

  handleClose = () => {
    if (!this.props.location.key || this.props.location.hash === '#new') {
      this.props.history.push('/publications');
      return;
    }

    this.props.history.goBack();
  };

  showPinModal = () => {
    this.setState({ isPinModalOpen: true });
  };

  handlePinModalClose = () => {
    this.setState({ isPinModalOpen: false });
  };

  handleSave = async ({
    data,
    exit,
    message,
    pinConfirmed,
    isContentValid = true,
  } = {}) => {
    if (!isContentValid) {
      Alert.error(
        'Есть неправильно или неполностью заполненные блоки в контенте'
      );
      return;
    }

    this.setState({ loadingSave: true });

    try {
      const response = await Posts.patch({
        publicationId: this.props.match.params.publicationId,
        body: { data },
        include: 'image,item_snoozes.snooze',
        pinConfirmed,
      });

      const publication = await new Deserializer({
        keyForAttribute: 'camelCase',
      }).deserialize(response);

      this.setState({
        loadingSave: false,
        publication,
        isPinModalOpen: false,
        shouldUnlockOnUnmount: this.props.can({
          permission: 'lock',
          resource: 'publication',
          state: publication.state,
        }),
      });

      Alert.success(message || 'Публикация успешно сохранена');

      if (exit) this.handleClose();
    } catch (err) {
      this.setState({ loadingSave: false });

      if (await isCode4(err)) {
        this.showPinModal(data, exit);
        return;
      }

      processErrorByAlert(err);
    }
  };

  handleDelete = () => {
    this.setState({ shouldUnlockOnUnmount: false }, () => {
      this.props.history.push('/publications');
    });
  };

  async loadPublication() {
    this.setState({ loading: true, errors: [] });

    try {
      const response = await Posts.get({
        publicationId: this.props.match.params.publicationId,
        include: 'image,item_snoozes.snooze',
      });

      const publication = await new Deserializer({
        keyForAttribute: 'camelCase',
      }).deserialize(response);

      if (
        publication.locked &&
        publication.lockedEmployeeId !== this.props.profile.id
      ) {
        Alert.error(
          `Публикация занята сотрудником ${publication.lockedEmployeeFullname}`
        );
        this.handleClose();

        return;
      }

      this.setState({
        publication,
        loading: false,
        shouldUnlockOnUnmount: this.props.can({
          permission: 'lock',
          resource: 'publication',
          state: publication.state,
        }),
      });
    } catch (err) {
      const errors = await getErrors(err);
      this.setState({ loading: false, errors });
    }
  }

  render() {
    return (
      <Main>
        <Helmet>
          <title>{this.title}</title>
        </Helmet>

        {this.state.publication && (
          <PublicationForm
            id={this.state.publication.id}
            title={this.state.publication.title}
            subtitle={this.state.publication.subtitle}
            shareTitle={this.state.publication.shareTitle}
            shareDescription={this.state.publication.shareDescription}
            chatTitle={this.state.publication.chatTitle}
            state={this.state.publication.state}
            content={this.state.publication.content}
            isPinned={this.state.publication.isPinned}
            isPush={this.state.publication.isPush}
            isAdult={this.state.publication.isAdult}
            isAuthorizedOnly={this.state.publication.isAuthorizedOnly}
            isNoise={this.state.publication.isNoise}
            isPartner={this.state.publication.isPartner}
            postType={this.state.publication.postType}
            positiveReaction={this.state.publication.positiveReaction}
            negativeReaction={this.state.publication.negativeReaction}
            destination={this.state.publication.destination}
            publishedAt={this.state.publication.publishedAt * 1000}
            isEditorApproved={this.state.publication.isEditorApproved}
            isCorrectorApproved={this.state.publication.isCorrectorApproved}
            onSave={this.handleSave}
            loading={this.state.loadingSave}
            image={this.state.publication.image}
            mascotType={this.state.publication.mascotType}
            mascotPhrase={this.state.publication.mascotPhrase}
            isMascotActive={this.state.publication.isMascotActive}
            emotion={this.state.publication.emotion}
            snoozes={this.snoozes}
            author={this.state.publication.author}
            sourceLink={this.state.publication.sourceLink}
            onClose={this.handleClose}
            isPinModalOpen={this.state.isPinModalOpen}
            onPinModalClose={this.handlePinModalClose}
            onDelete={this.handleDelete}
            shouldUnlockOnUnmount={this.state.shouldUnlockOnUnmount}
          />
        )}

        {this.state.loading && (
          <CenteredContainer style={{ width: '100%' }}>
            <Loader />
          </CenteredContainer>
        )}

        {this.state.errors.length > 0 && (
          <CenteredContainer style={{ width: '100%' }}>
            {this.state.errors.map((error) => (
              <ErrorText role="alert" key={error}>
                {error}
              </ErrorText>
            ))}
          </CenteredContainer>
        )}
      </Main>
    );
  }
}

PublicationPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      publicationId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
    push: PropTypes.func,
  }),
  location: PropTypes.shape({
    key: PropTypes.string,
    hash: PropTypes.string,
  }),
  profile: PropTypes.shape({ id: PropTypes.string }),
  can: PropTypes.func,
};

PublicationPage.defaultProps = {
  history: {
    goBack: () => {},
    push: () => {},
  },
  location: {},
  profile: {},
  can: () => {},
};

export default withProfile(withRouter(PublicationPage));
